/**
 * Normalizes speaker name references in an analysis JSON object.
 *
 * The analysis is generated at processing time and may contain raw names like
 * "Dave (Speaker_1)" or bare "Speaker_N" patterns. When a speaker is later
 * named, we patch those strings client-side from the unified `speakers[]`
 * read model: each raw diarisation label maps to its display name when the
 * speaker is named, or to "Speaker N" (the unified display number) when not.
 *
 * A positional `speaker_names[N]` lookup used to be the only path, and it
 * depended on detecting whether the provider was 0- or 1-indexed. The label
 * map makes that index arithmetic unnecessary for meetings that carry
 * `speakers[]`. The positional helper remains for legacy meetings that don't.
 *
 * Label shapes accepted:
 *   - "Speaker_1"     — digit-only diarization label
 *   - "Speaker_S1"    — newer S-prefixed shape from updated transcription
 *                       providers
 *   - "SPEAKER_01"    — uppercase + zero-padded variant
 */

import { detectIndexBase, isRawSpeakerLabel, RAW_SPEAKER_LABEL_SOURCE, type TIndexBase } from "@lexicanon/shared"

// Re-export the shared base type + detector so existing consumers keep importing
// from speaker-mapping.ts (one frontend entry point for label utilities).
export { detectIndexBase } from "@lexicanon/shared"

const REMOTE_PREFIX = "Remote::"

// Bare "Speaker_N" / "Speaker_S1" occurrence anywhere in a string — built from
// the shared label source so the accepted shapes stay in lockstep with the
// backend. Global + case-insensitive for the deep-string replacement pass.
const SPEAKER_LABEL = new RegExp(`\\b${RAW_SPEAKER_LABEL_SOURCE}\\b`, "gi")
// "Name (Speaker_X)" — capture the name in group 1, the X in group 2 (unused).
// We strip the parens regardless of the speakerNames lookup: the LLM already
// inferred a name from context and put it before the parens, so the parens
// payload is just the LLM's "showing its work" trace, not a source of truth.
const PARENTHESISED_SPEAKER = /\s*\(Speaker_S?\d+[^)]*\)/gi

/** The fields {@link speakerLabelDisplayMap} reads off a unified speaker entry. */
export interface ISpeakerLabelSource {
  label: string | null
  displayName: string
  displayIndex: number
  status: string
}

function isNamedSpeaker(entry: ISpeakerLabelSource): boolean {
  return entry.status !== "unidentified" && entry.status !== "suggested" && !isRawSpeakerLabel(entry.displayName)
}

function unnamedDisplay(entry: ISpeakerLabelSource): string {
  if (entry.displayIndex > 0) return `Speaker ${entry.displayIndex}`
  return entry.displayName.replace(/_/g, " ")
}

function putLabel(map: Map<string, string>, label: string, display: string): void {
  if (!map.has(label)) map.set(label, display)
  if (label.startsWith(REMOTE_PREFIX)) {
    const core = label.slice(REMOTE_PREFIX.length)
    if (!map.has(core)) map.set(core, display)
  }
}

/**
 * Build the raw-label → display-name map from a meeting's unified `speakers[]`.
 * Named speakers map to their display name; unidentified / suggested slots map
 * to "Speaker N" via {@link ISpeakerLabelSource.displayIndex}.
 */
export function speakerLabelDisplayMap(speakers: readonly ISpeakerLabelSource[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const entry of speakers) {
    if (entry.label === null || entry.label.length === 0) continue
    const display = isNamedSpeaker(entry) ? entry.displayName : unnamedDisplay(entry)
    putLabel(map, entry.label, display)
  }
  return map
}

/**
 * Positional fallback for meetings served without `speakers[]`: `Speaker_S{n}`
 * (and the other accepted shapes) map to `speakerNames[n - indexBase]`.
 */
export function positionalSpeakerLabelMap(
  speakerNames: readonly string[],
  indexBase: TIndexBase = 1,
): Map<string, string> {
  const map = new Map<string, string>()
  speakerNames.forEach((name, i) => {
    if (name.length === 0) return
    const n = i + indexBase
    const padded = String(n).padStart(2, "0")
    putLabel(map, `Speaker_${n}`, name)
    putLabel(map, `Speaker_S${n}`, name)
    putLabel(map, `SPEAKER_${padded}`, name)
  })
  return map
}

function lookupLabel(map: Map<string, string>, raw: string): string | undefined {
  const direct = map.get(raw)
  if (direct !== undefined) return direct
  const lower = raw.toLowerCase()
  for (const [key, value] of map) {
    if (key.toLowerCase() === lower) return value
  }
  return undefined
}

function replaceNames(value: string, labelMap: Map<string, string>): string {
  // First strip every "(Speaker_X)" / "(Speaker_S1)" suffix the LLM appended
  // to a speaker name. This catches "Gerard (Speaker_S1)" → "Gerard" without
  // depending on a roster being aligned to diarization indices.
  let result = value.replace(PARENTHESISED_SPEAKER, "")
  // Then replace any remaining bare "Speaker_N" / "Speaker_S1" via the label
  // map. If the label has no resolved name, leave it intact rather than
  // inventing a wrong one.
  result = result.replace(SPEAKER_LABEL, (match) => lookupLabel(labelMap, match) ?? match)
  return result
}

// Keys whose value is a RAW transcript label, not display prose. Normalization
// must leave them byte-identical — rewriting "Speaker_0" here would erase the
// very label the fusion step keys on to map the analysis attendee guess back to
// the right speaker (Wave D).
const RAW_LABEL_KEYS = new Set<string>(["speaker_label"])

function deepNormalize(value: unknown, labelMap: Map<string, string>): unknown {
  if (typeof value === "string") return replaceNames(value, labelMap)
  if (Array.isArray(value)) return value.map((v) => deepNormalize(v, labelMap))
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = RAW_LABEL_KEYS.has(k) ? v : deepNormalize(v, labelMap)
    }
    return result
  }
  return value
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}

export function normalizeAnalysis(
  analysis: Record<string, unknown>,
  labelMap: Map<string, string>,
): Record<string, unknown> {
  // We always run the parens-stripping pass even when the map is empty,
  // because the "Name (Speaker_X)" → "Name" transform doesn't need names.
  const result = deepNormalize(analysis, labelMap)
  return isPlainRecord(result) ? result : analysis
}

/**
 * Map a raw diarization label like "Speaker_S1" to its resolved display name
 * via a label map, falling back to a positional roster + index base when the
 * caller still has only `speaker_names`. Returns the input unchanged if the
 * label doesn't match or no resolved name exists.
 */
export function resolveSpeakerLabel(
  rawLabel: string,
  speakerNamesOrMap: readonly string[] | Map<string, string>,
  indexBase: TIndexBase = 1,
): string {
  if (speakerNamesOrMap instanceof Map) return lookupLabel(speakerNamesOrMap, rawLabel) ?? rawLabel
  const map = positionalSpeakerLabelMap(speakerNamesOrMap, indexBase)
  return lookupLabel(map, rawLabel) ?? rawLabel
}

/**
 * Project a segments array onto resolved speaker names. Other fields pass
 * through unchanged. When the map / roster is empty, returns the input array
 * by reference (no allocation).
 */
export function normalizeSegmentSpeakers<TSeg extends { speaker: string }>(
  segments: TSeg[] | undefined,
  speakerNamesOrMap: readonly string[] | Map<string, string>,
): TSeg[] | undefined {
  if (!segments?.length) return segments
  const map =
    speakerNamesOrMap instanceof Map
      ? speakerNamesOrMap
      : speakerNamesOrMap.length > 0
        ? positionalSpeakerLabelMap(speakerNamesOrMap, detectIndexBase(segments))
        : null
  if (map === null || map.size === 0) return segments
  return segments.map((seg) => {
    const mapped = lookupLabel(map, seg.speaker)
    return mapped !== undefined ? { ...seg, speaker: mapped } : seg
  })
}

/**
 * Render a speaker label for DISPLAY only. Far-end (system / remote-channel)
 * speakers carry a `Remote::` identity prefix that keeps them distinct from the
 * room mic's speakers (distinct waveform colour, separate identity). For display
 * we strip the technical prefix and tag them "(far end)" so the UI reads cleanly
 * ("Speaker_S1 (far end)", or "Bob (far end)" once named). The raw prefixed label
 * stays the colour/identity/rename key — never use this for lookups or mapping.
 */
export function formatSpeakerLabel(name: string): string {
  return name.startsWith(REMOTE_PREFIX) ? `${name.slice(REMOTE_PREFIX.length)} (far end)` : name
}

/**
 * True when a speaker label is still a raw diarization placeholder needing human
 * assignment — including the far-end `Remote::Speaker_N` / `Remote::unknown`
 * variants (so the far-end speaker is flagged for identification + rename like
 * any other, instead of being mistaken for an already-named speaker).
 */
export function isUnidentifiedSpeaker(name: string): boolean {
  const core = name.startsWith(REMOTE_PREFIX) ? name.slice(REMOTE_PREFIX.length) : name
  // `s?` matches the S-prefixed shape providers emit ("Speaker_S1"), not just
  // "Speaker_1" — the old check missed those and left them un-flagged.
  return /^speaker[_\s-]*s?\d+/i.test(core) || core.toLowerCase() === "unknown"
}

export interface ISpeakerQuoteGroup {
  speaker: string
  quotes: string[]
}

function isSpeakerQuoteGroup(value: unknown): value is ISpeakerQuoteGroup {
  return (
    typeof value === "object" &&
    value !== null &&
    "speaker" in value &&
    typeof value.speaker === "string" &&
    "quotes" in value &&
    Array.isArray(value.quotes)
  )
}

/**
 * Coerce the analysis `speaker_quotes` field to a well-formed array.
 *
 * The documented shape is `[{ speaker, quotes }]`, but the analysis LLM
 * occasionally double-nests it as `{ speaker_quotes: [...] }`. Iterating that
 * object directly (`for (const q of analysis.speaker_quotes ?? [])`) threw
 * "object is not iterable" and white-screened the entire meeting page. LLM
 * output is untrusted-shape, so every consumer goes through this guard: a bare
 * array passes through, the `{ speaker_quotes: [...] }` wrapper is unwrapped,
 * malformed entries are dropped, and anything else yields `[]`.
 */
export function coerceSpeakerQuotes(raw: unknown): ISpeakerQuoteGroup[] {
  if (Array.isArray(raw)) return raw.filter(isSpeakerQuoteGroup)
  if (typeof raw === "object" && raw !== null && "speaker_quotes" in raw && Array.isArray(raw.speaker_quotes)) {
    return raw.speaker_quotes.filter(isSpeakerQuoteGroup)
  }
  return []
}
